import { useState, MouseEvent } from "react";
import Button from "@mui/material/Button";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Table as CustomTable } from "../../../components/Table";
import { useStyles } from "./styles";
import { DELETE_RED } from "../../../utils/colors";
import { convertToBase64 } from "../../../utils/base64";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ModalGlobalStore from "./ModalGlobalStore";
import { DeleteStore, Store, InputStore } from "../../../types/stores";
import { useToast } from "../../../hooks/useToast";
import { isValidURL } from "../../../utils/validations";
import { AlertDialog } from "../../../components/AlertDialog";
import CircularProgress from "@mui/material/CircularProgress";
import { Box } from "@mui/material";
import {
  createStore,
  createStoreLogo,
  deleteStore,
  listStores,
  updateStore,
} from "../../../services/stores";

interface IStores {}

export function Stores(props: IStores) {
  const [typeModal, setTypeModal] = useState("");
  const [isOpenAlertDialog, setIsOpenAlertDialog] = useState(false);
  const [openGlobalModal, setOpenGlobalModal] = useState(false);
  const styles = useStyles();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rowEdit, setRowEdit] = useState({} as Store);
  const [storeDelete, setStoreDelete] = useState(
    {} as { id_loja: number; nome_loja: string }
  );

  const { data: stores, isLoading: loadingStores } = useQuery(
    "listStores",
    async () => {
      const stores: Store[] = await listStores();
      return stores;
    },
    {
      staleTime: 10 * 1000,
      cacheTime: 10 * 1000,
    }
  );

  const createStoreMutation = useMutation(createStore, {
    onSuccess: async () => {
      await queryClient.invalidateQueries("listStores");
      toast.show("Sua nova loja foi criada!", { severity: "success" });
    },
    onError: (err) => {
      console.log("create store", err);
      toast.show("Não foi possivel criar a sua loja!", { severity: "error" });
      //setOpenGlobalModal(false);
    },
  });

  const createStoreLogoMutation = useMutation(createStoreLogo, {
    onError: (err) => {
      toast.show(`Ocorreu um erro na criação da imagem!`, {
        severity: "error",
      });
      console.log("create store logo", err);
    },
  });

  const updateStoreMutation = useMutation(
    async (store: Store) => {
      const response = await updateStore(store);
      return response;
    },
    {
      onSuccess: async (message) => {
        console.log("message", message);
        queryClient.invalidateQueries("listStores");
        toast.show(`Sua loja foi atualizada com sucesso!`, {
          severity: "success",
        });
      },
      onError: (err) => {
        console.log("delete store", err);
        toast.show(`Não foi possivel atualizar sua loja!`, {
          severity: "error",
        });
      },
    }
  );

  const deleteStoreMutation = useMutation(deleteStore, {
    onSuccess: async () => {
      toast.show(`${storeDelete.nome_loja} foi excluído com sucesso!`, {
        severity: "success",
      });

      handleCloseAlertDialog();
      setStoreDelete({} as DeleteStore);
      await queryClient.invalidateQueries("listStores");
    },
    onError: (err) => {
      console.log("delete store", err);
    },
  });

  const handleOpenAlertDialog = () => {
    setIsOpenAlertDialog(true);
  };

  const handleCloseAlertDialog = () => {
    setIsOpenAlertDialog(false);
  };

  function handleStoreDelete(store: DeleteStore) {
    deleteStoreMutation.mutate(store);
  }

  async function handleSaveStore(store: InputStore) {
    const factorySaveStore: Store = {
      id_loja: store.store_id,
      bairro: store.district,
      cep: store.cep,
      cidade: store.city,
      descricao: store.description,
      endereco: store.address,
      frete: store.shipping
        ? parseFloat(store.shipping.replace(",", "."))
        : undefined,
      maximo_item: store.max_items,
      minimo_compra: store.min_purchases
        ? parseFloat(store.min_purchases.replace(",", "."))
        : undefined,
      nome_loja: store.store_name,
      numero: store.address_number,
      tipo_pedido: store.shipping_type,
      taxa_servico: store.service_tax
        ? parseFloat(store.service_tax.replace(",", "."))
        : undefined,
      uf: store.uf,
      whatsapp_phone_number: store.whatsapp,
      aceita_dinheiro: store.receive_cash,
      aceita_debito: store.receive_debit,
      aceita_credito: store.receive_credit,
      aceita_boleto: store.receive_billet,
      aceita_vr: store.receive_meal_ticket,
      aceita_pix: store.receive_pix,
      chave_pix: store.pix_key,
      logo1: store.base64,
      naoRemoverFundo: store.is_not_remove_background,
    };

    try {
      try {
        if (
          factorySaveStore.logo1 &&
          /;base64,/g.test(factorySaveStore.logo1!)
        ) {
          const newImage = await createStoreLogoMutation.mutateAsync({
            imagem_produto: factorySaveStore.logo1,
            isBase64: true,
            naoRemoverFundo: factorySaveStore.naoRemoverFundo,
            isLogo: true,
          });

          if (newImage?.image) {
            factorySaveStore.logo1 = newImage.image;
          } else {
            throw new Error("The property image of 'newImage' is null");
          }
        }
      } catch (err) {
        toast.show(
          `Não foi possível adicionar a logo. Tente novamente depois.`,
          {
            severity: "error",
          }
        );
      }
      await createStoreMutation.mutateAsync(factorySaveStore);
    } catch (err) {
      console.log(err);
    }
  }

  async function handleEditStore(store: InputStore) {
    try {
      const factoryEditStore: Store = {
        id_loja: store.store_id,
        bairro: store.district,
        cep: store.cep,
        cidade: store.city,
        descricao: store.description,
        endereco: store.address,
        frete: store.shipping
          ? parseFloat(store.shipping.replace(",", "."))
          : undefined,
        maximo_item: store.max_items,
        minimo_compra: store.min_purchases
          ? parseFloat(store.min_purchases.replace(",", "."))
          : undefined,
        nome_loja: store.store_name,
        numero: store.address_number,
        tipo_pedido: store.shipping_type,
        taxa_servico: store.service_tax
          ? parseFloat(store.service_tax.replace(",", "."))
          : undefined,
        uf: store.uf,
        whatsapp_phone_number: store.whatsapp,
        aceita_dinheiro: store.receive_cash,
        aceita_debito: store.receive_debit,
        aceita_credito: store.receive_credit,
        aceita_boleto: store.receive_billet,
        aceita_vr: store.receive_meal_ticket,
        aceita_pix: store.receive_pix,
        chave_pix: store.pix_key,
        logo1: store.base64,
        naoRemoverFundo: store.is_not_remove_background,
      };

      let newImage = "";
      if (
        Boolean(factoryEditStore.logo1) &&
        /;base64,/g.test(factoryEditStore.logo1!)
      ) {
        newImage = await createStoreLogoMutation.mutateAsync({
          imagem_produto: factoryEditStore.logo1,
          isBase64: true,
          naoRemoverFundo: Boolean(factoryEditStore.naoRemoverFundo),
          isLogo: true,
        });
      }

      let dateNow: Date | string = new window.Date();
      dateNow = dateNow.getTime().toString();

      if (isValidURL(newImage)) {
        newImage = newImage.split("?")[0] + "?" + dateNow;
      }

      factoryEditStore.logo1 = newImage;

      factoryEditStore.frete = factoryEditStore.frete
        ? parseFloat(factoryEditStore.frete.toString().replace(",", "."))
        : undefined;

      factoryEditStore.minimo_compra = factoryEditStore.minimo_compra
        ? parseFloat(
            factoryEditStore.minimo_compra.toString().replace(",", ".")
          )
        : undefined;

      factoryEditStore.taxa_servico = factoryEditStore.minimo_compra
        ? parseFloat(
            factoryEditStore.taxa_servico!.toString().replace(",", ".")
          )
        : undefined;

      await updateStoreMutation.mutateAsync(factoryEditStore);
    } catch (err) {
      console.log(err);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        <Button
          onClick={() => {
            setTypeModal("add");
            setOpenGlobalModal(true);
          }}
          size="large"
          className={styles.button}
          color="primary"
          variant="contained"
        >
          Adicionar nova loja
        </Button>
      </div>
      <div className={styles.content}>
        <CustomTable
          toolbar={false}
          data={stores || []}
          isLoading={loadingStores}
          pageSize={10}
          //@ts-ignore:next-line
          paging
          onRowClick={(
            evt: MouseEvent<Element, globalThis.MouseEvent> | undefined,
            selectedRow
          ) => {
            //@ts-ignore:next-line
            if (evt!.target.id !== "deleteRow") {
              setRowEdit(() => selectedRow);
              setTypeModal("edt");
              console.log("select: ", selectedRow);
              setOpenGlobalModal(true);
            }
          }}
          style={{
            width: "100%",
          }}
          headerStyle={{
            backgroundColor: "#F4F3FB",
            color: "#1D1D1D",
            zIndex: 0,
            padding: "25px 0 25px 25px",
            textAlign: "center",
          }}
          rowStyle={{
            width: "100%",
            maxHeight: "80px",
            backgroundColor: "#FFFFFF",
            color: "#707070",
            lineHeight: "22px",
            fontWeight: "500",
            fontFamily: "Montserrat",
          }}
          columns={[
            {
              field: "logo1",
              title: "Logo",
              align: "center",
              editComponent: (props) => (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: "#e7e7e7",
                  }}
                >
                  <img
                    alt="logo"
                    src={
                      props.value
                        ? props.value.includes("data:")
                          ? props.value
                          : props.value.split("?")[0] +
                            "?" +
                            Math.random().toString()
                        : null ||
                          "https://datasalesio-imagens.s3.amazonaws.com/imagem-na%CC%83o-disponivel-png-3.png"
                    }
                    style={{ width: 70 }}
                  />
                  <div style={{ marginTop: "2vh", textAlign: "center" }}>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="change-image"
                      type="file"
                      onChange={async ({ target: { files } }) => {
                        const imageEdit = await convertToBase64(files![0]);
                        props.onChange(imageEdit.base64);
                      }}
                    />
                    <label htmlFor="change-image">
                      <Button size="small" component="span">
                        Escolher foto
                      </Button>
                    </label>
                  </div>
                </Box>
              ),
              render: (rowData) => (
                <img
                  alt={rowData.logo1}
                  src={
                    rowData.logo1
                      ? rowData.logo1.split("?")[0] +
                        "?" +
                        Math.random().toString()
                      : null ||
                        "https://datasalesio-imagens.s3.amazonaws.com/imagem-na%CC%83o-disponivel-png-3.png"
                  }
                  style={{
                    width: 70,
                    height: 80,
                    borderColor: "white",
                  }}
                />
              ),
            },
            {
              title: "Loja",
              field: "nome_loja",
              editable: "never",
              align: "center",
              cellStyle: {
                textAlign: "center",
                // background: 'blue'
              },
            },
            {
              title: "Whatsapp",
              field: "whatsapp_phone_number",
              align: "center",
            },
            {
              title: "CEP",
              field: "cep",
              align: "center",
            },
            {
              title: "Endereco",
              field: "endereco",
              align: "center",
            },
            {
              title: "Bairro",
              field: "bairro",
              align: "center",
            },

            {
              title: "Cidade",
              field: "cidade",
              align: "center",
            },
            {
              title: "",
              field: "deleteRow",
              align: "center",
              disableClick: true,
              render: (rowData: DeleteStore) => (
                <Box
                  onClick={() => {
                    const store = {
                      id_loja: rowData.id_loja,
                      nome_loja: rowData.nome_loja,
                    };

                    setStoreDelete(() => store);
                    handleOpenAlertDialog();
                  }}
                  style={{
                    padding: 0,
                    width: "100%",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <DeleteOutlinedIcon
                    id={"deleteRow"}
                    style={{
                      display: "flex",
                      color: DELETE_RED,
                    }}
                  />
                </Box>
              ),
            },
          ]}
        />
      </div>

      <AlertDialog
        loading={deleteStoreMutation.isLoading}
        open={isOpenAlertDialog}
        title={"Tem certeza que deseja excluir esta loja?"}
        contentText={"Se você confirmar, todas as informações serão excluídas."}
        textAgree={
          deleteStoreMutation.isLoading ? (
            <CircularProgress size={24} sx={{ color: "#FFF" }} />
          ) : (
            "Sim, excluir"
          )
        }
        textClose={"Cancelar"}
        handleClose={() => {
          handleCloseAlertDialog();
        }}
        handleAgree={() => {
          handleStoreDelete(storeDelete);
        }}
      />

      {openGlobalModal && (
        <ModalGlobalStore
          showModal={openGlobalModal}
          data={rowEdit}
          onSave={async (store) => {
            try {
              if (typeModal === "add") {
                await handleSaveStore(store);
              } else {
                if (typeModal === "edt") {
                  await handleEditStore(store);
                }
              }
            } catch (err) {
              console.log(err);
            }
          }}
          onClose={() => {
            setRowEdit({} as Store);
            setOpenGlobalModal(false);
          }}
        />
      )}
    </div>
  );
}
